import { ApiError } from '../../api/client.ts';

/** Readable names of endpoint fields in server problems (`headers.0.value`, `signingSecretRef`, …). */
export function fieldLabel(path: string | undefined): string {
  if (!path) return 'a field';
  const [head, index, leaf] = path.split('.');
  const n = index !== undefined && /^\d+$/.test(index) ? ` ${Number(index) + 1}` : '';
  switch (head) {
    case 'url':
      return 'the URL';
    case 'headers':
      return `header${n}${leaf === 'name' ? ' name' : leaf === 'value' ? ' value' : ''}`;
    case 'secretHeaders':
      return `secret header${n}${leaf === 'name' ? ' name' : leaf === 'secretRef' ? ' reference' : ''}`;
    case 'signingSecretRef':
      return 'the signing secret reference';
    case 'params':
      return `param${n}${leaf ? ` ${leaf}` : ''}`;
    case 'allowedDomains':
      return `domain${n}`;
    default:
      return head ?? 'a field';
  }
}

const SECRET_REASONS: Record<string, string> = {
  secret_missing: 'the variable or file is not set on the server',
  secret_scheme_unsupported: 'use env:NAME or file:/path',
  secret_path_forbidden: 'the file must be inside the service’s secrets directory',
};

/**
 * Problems of the webhook settings API (BB-142) in words; the server's detail and code stay visible so a failed
 * request can be traced.
 */
export function webhookProblemText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong.';
  const p = error.problem as { detail?: string; field?: string; reason?: string; issues?: Array<{ path?: string; message?: string }> };
  const detail = p.detail ? ` (${p.detail})` : '';
  switch (error.code) {
    case 'webhook_domain_not_allowed':
      return `The host is not in this environment’s allowed domains: add it under Allowed domains first${detail}.`;
    case 'webhook_https_required':
      return `The URL must use https://${detail}.`;
    case 'webhook_address_blocked':
      return `The host is a private or local address: webhooks call public hosts only${detail}.`;
    case 'webhook_url_invalid':
      return `The URL is not valid${detail}.`;
    case 'template_invalid':
      return `A template in ${fieldLabel(p.field)} does not compile${detail}. Liquid may read only declared params as {{ params.<name> }}.`;
    case 'webhook_secret_invalid':
      return `The secret reference in ${fieldLabel(p.field)} does not resolve: ${SECRET_REASONS[p.reason ?? ''] ?? p.reason ?? 'unknown reason'}. Secrets are set on the server, never here.`;
    case 'webhook_endpoint_name_taken':
      return 'An endpoint with this name exists: choose another name.';
    case 'webhook_endpoint_not_found':
      return 'This endpoint no longer exists (archived meanwhile?). Reload the list.';
    case 'validation_failed':
      return p.issues?.length ? `Check the form: ${p.issues.map((i) => `${fieldLabel(i.path)} — ${i.message ?? 'invalid'}`).join('; ')}.` : `Check the form${detail}.`;
    default:
      if (error.status === 403) return 'Managing webhooks needs the integrations.manage permission.';
      return `${error.message} (${error.code})`;
  }
}
